/**
 * ============================================================================
 * Registro central de atalhos do App Launcher (estilo Gmail)
 * ----------------------------------------------------------------------------
 * RELEASE: 2026-10-18 v1.6.0 - Novos blocos Ordem de Serviço, Gestão Rastreador e
 *   Logística (mesmas rotas/menuKeys do menu lateral).
 * RELEASE: 2026-10-02 v1.5.0 - Atualização da lista: novos módulos Veículos,
 *   Retenção, Jurídico, Workflow, Dados, Distribuição/Conteúdo e páginas novas
 *   de CRM/Financeiro/Indicação/Configuração, com o mesmo gate do menu lateral.
 * RELEASE: 2026-07-24 v1.4.0 - Blindagem de permissões: adiciona
 *   `requireAdmin?: boolean` na interface e aplica em atalhos de rotas
 *   admin-only (Configurações, Permissões, Empresa). Removido `inboxes` do
 *   atalho Templates (rota agora aberta a todos). Sem regressões: todos os
 *   demais atalhos permanecem inalterados.
 * RELEASE: 2026-07-24 v1.3.0 - Categoria "Atividades" separada de Negócio.
 *   `activities_feed` renomeado para "Dashboard Atividades" (rota
 *   /tenant/activities/dashboard). Novos atalhos: Lista Atividades,
 *   Feed Atividades, Agenda Atividades.
 * RELEASE: 2026-07-24 v1.2.0 - (1) Labels de Dashboard/Kanban passam a
 *   incluir o módulo (Dashboard Negócios, Kanban Tarefas, etc.) para que o
 *   usuário identifique o atalho mesmo quando promovido aos favoritos.
 *   (2) Todos os atalhos com página gerenciada por permissão granular
 *   receberam `menuKeys` oficiais (idênticos aos usados no sidebar via
 *   AVAILABLE_MENUS em usePermissions.ts) — o launcher agora respeita o
 *   MESMO gate dos menus laterais. (3) Novos atalhos: Ações rápidas/Macros
 *   (routing), Metas, Vendedores, Playbook, Motivos Perdas, Origem Leads,
 *   Workspace. (4) Categoria Indicação reorganizada.
 * RELEASE: 2026-07-24 v1.1.0 - Categorização dos atalhos em blocos temáticos.
 * RELEASE: 2026-07-24 v1.0.1 - Correção cirúrgica das rotas.
 * RELEASE: 2026-07-24 v1.0.0 - Criação.
 * ============================================================================
 */
import type { LucideIcon } from 'lucide-react';
import {
  MessageSquare,
  Users,
  Briefcase,
  Package,
  ClipboardList,
  Kanban,
  LayoutDashboard,
  Wallet,
  Receipt,
  CreditCard,
  CalendarClock,
  Calendar,
  Megaphone,
  BookOpen,
  Activity,
  Settings,
  FileSignature,
  ListChecks,
  Contact2,
  Send,
  Workflow,
  Handshake,
  ListOrdered,
  BellRing,
  FileText,
  Inbox,
  Repeat,
  Layers,
  Link2,
  AlarmClock,
  UserPlus,
  Percent,
  ShieldCheck,
  Building2,
  Route,
  Target,
  UserCheck,
  XCircle,
  Image as ImageIcon,
  Car,
  ClipboardCheck,
  MapPin,
  Tag,
  Scale,
  GitBranch,
  Database,
  Search,
  Zap,
  History,
  Download,
  // v1.6.0
  Wrench,
  List,
  Tags,
  Shield,
  Cpu,
  MapPinned,
  BadgeCheck,
  Monitor,
  Ban,
  Truck,
  Boxes,
} from 'lucide-react';

/** Módulos aos quais um atalho pode estar vinculado (para gate por tenant). */
export type ShortcutModule =
  | 'conversations'
  | 'crm'
  | 'financial'
  | 'tasks'
  | 'referral'
  | 'activities'
  | 'knowledge'
  // v1.5.0: módulos criados após o launcher
  | 'vehicles'
  | 'retention'
  | 'legal'
  | 'workflow'
  // v1.6.0
  | 'serviceOrders'
  | 'stock'
  | 'logistics'
  | 'shared';

/** Definição de um atalho no launcher. */
export interface AppShortcutDef {
  /** Identificador estável, também usado nas preferências salvas. */
  key: string;
  /** Rótulo curto exibido no tile. */
  label: string;
  /** Ícone lucide. */
  icon: LucideIcon;
  /** Destino da navegação (react-router). */
  route: string;
  /** Módulo do tenant necessário (usado por useAvailableShortcuts). */
  module: ShortcutModule;
  /**
   * Ao menos uma dessas menu_keys precisa estar liberada para o usuário.
   * Se vazio, basta o módulo estar habilitado (ou admin).
   * IMPORTANTE: usar as MESMAS keys de AVAILABLE_MENUS (src/hooks/usePermissions.ts)
   * para que o launcher respeite o mesmo gate dos menus laterais.
   */
  menuKeys?: string[];
  /**
   * Se true, apenas root_admin / tenant_admin vê o atalho.
   * Usado para rotas que já usam `requireAdmin` no PermissionGuard
   * (Configurações, Permissões, Empresa, etc.).
   */
  requireAdmin?: boolean;
  /** Cor de destaque do tile (Tailwind text-*). */
  accent?: string;
}

/**
 * Lista canônica de atalhos. Fonte única de metadados (label/ícone/rota/módulo).
 * A ORDEM de exibição no popover é definida por `SHORTCUT_CATEGORIES` abaixo.
 */
export const APP_SHORTCUTS: AppShortcutDef[] = [
  // ---------------- Conversas / Compartilhado ----------------
  { key: 'chat_inbox',         label: 'Conversas',            icon: MessageSquare,   route: '/tenant/chat',                  module: 'conversations', menuKeys: ['chat'],               accent: 'text-sky-600' },
  { key: 'chat_kanban',        label: 'Kanban Conversas',     icon: Kanban,          route: '/tenant/kanban',                module: 'conversations', menuKeys: ['kanban'],             accent: 'text-sky-600' },
  { key: 'chat_dashboard',     label: 'Dashboard Conversas',  icon: LayoutDashboard, route: '/tenant',                       module: 'conversations', menuKeys: ['dashboard'],          accent: 'text-sky-600' },
  { key: 'contacts',           label: 'Contatos',             icon: Contact2,        route: '/tenant/contacts',              module: 'shared',        menuKeys: ['contacts'],           accent: 'text-sky-600' },
  { key: 'lists',              label: 'Importar Listas',      icon: ListChecks,      route: '/tenant/lists?tab=contacts',    module: 'shared',        menuKeys: ['lists_contacts','lists_deals','lists_customers','lists_affiliates'], accent: 'text-sky-600' },
  { key: 'campaigns',          label: 'Campanhas',            icon: Send,            route: '/tenant/campaigns',             module: 'conversations', menuKeys: ['campaigns'],          accent: 'text-sky-600' },
  { key: 'flows',              label: 'Fluxos',               icon: Workflow,        route: '/tenant/flows',                 module: 'conversations', menuKeys: ['flows'],              accent: 'text-sky-600' },
  { key: 'routing',            label: 'Ações Rápidas',        icon: Route,           route: '/tenant/routing',               module: 'shared',        menuKeys: ['routing'],            accent: 'text-sky-600' },
  { key: 'service_queue',      label: 'Fila Atendimento',     icon: ListOrdered,     route: '/tenant/queue',                 module: 'shared',        menuKeys: ['queue'],              accent: 'text-sky-600' },
  { key: 'calendar',           label: 'Calendário',           icon: Calendar,        route: '/tenant/calendar',              module: 'shared',                                          accent: 'text-sky-600' },
  { key: 'reminders_center',   label: 'Lembretes',            icon: BellRing,        route: '/tenant/reminders',             module: 'shared',        menuKeys: ['reminders_center'],   accent: 'text-sky-600' },
  { key: 'scheduled_messages', label: 'Msg. Agendadas',       icon: CalendarClock,   route: '/tenant/scheduled-messages',    module: 'shared',        menuKeys: ['scheduled_messages'], accent: 'text-sky-600' },
  { key: 'templates',          label: 'Templates',            icon: FileText,        route: '/tenant/templates',             module: 'shared',        menuKeys: ['templates'],          accent: 'text-sky-600' },
  { key: 'inboxes',            label: 'Inbox',                icon: Inbox,           route: '/tenant/inboxes',               module: 'shared',        menuKeys: ['inboxes'],            accent: 'text-sky-600' },

  // ---------------- Negócio (CRM) ----------------
  { key: 'crm_deals',          label: 'Negócios',             icon: Briefcase,       route: '/tenant/crm/deals',             module: 'crm', menuKeys: ['crm_deals'],                  accent: 'text-emerald-600' },
  { key: 'crm_dashboard',      label: 'Dashboard Negócios',   icon: LayoutDashboard, route: '/tenant/crm',                   module: 'crm', menuKeys: ['crm_dashboard'],              accent: 'text-emerald-600' },
  { key: 'crm_contracts',      label: 'Contratos',            icon: FileSignature,   route: '/tenant/crm/contracts',         module: 'crm', menuKeys: ['crm_contracts'],              accent: 'text-emerald-600' },
  { key: 'activities_feed',        label: 'Dashboard Atividades', icon: LayoutDashboard, route: '/tenant/activities/dashboard',    module: 'activities', menuKeys: ['activities_list'], accent: 'text-emerald-600' },
  { key: 'activities_list_page',   label: 'Lista Atividades',     icon: ListChecks,      route: '/tenant/activities/list',         module: 'activities', menuKeys: ['activities_list'], accent: 'text-emerald-600' },
  { key: 'activities_feed_page',   label: 'Feed Atividades',      icon: Activity,        route: '/tenant/crm/activities',          module: 'activities', menuKeys: ['activities_list'], accent: 'text-emerald-600' },
  { key: 'activities_calendar',    label: 'Agenda Atividades',    icon: CalendarClock,   route: '/tenant/crm/activities/calendar', module: 'activities', menuKeys: ['activities_list'], accent: 'text-emerald-600' },
  // v1.5.0: Agendamento Online vive dentro do CRM — atalho segue o módulo
  { key: 'scheduling',         label: 'Agendamento',          icon: CalendarClock,   route: '/tenant/scheduling',            module: 'crm', menuKeys: ['scheduling'],              accent: 'text-emerald-600' },
  { key: 'crm_customers',      label: 'Clientes',             icon: Users,           route: '/tenant/crm/customers',         module: 'crm', menuKeys: ['crm_customers'],              accent: 'text-emerald-600' },
  { key: 'crm_products',       label: 'Produtos',             icon: Package,         route: '/tenant/crm/products',          module: 'crm', menuKeys: ['crm_products'],               accent: 'text-emerald-600' },
  { key: 'crm_goals',          label: 'Metas',                icon: Target,          route: '/tenant/crm/goals',             module: 'crm', menuKeys: ['crm_goals'],                  accent: 'text-emerald-600' },
  { key: 'crm_sellers',        label: 'Vendedores',           icon: UserCheck,       route: '/tenant/crm/sellers',           module: 'crm', menuKeys: ['crm_sellers'],                accent: 'text-emerald-600' },
  { key: 'crm_playbooks',      label: 'Playbook',             icon: ListChecks,      route: '/tenant/crm/playbooks',         module: 'crm', menuKeys: ['crm_playbooks'],              accent: 'text-emerald-600' },
  { key: 'crm_loss_reasons',   label: 'Motivos Perdas',       icon: XCircle,         route: '/tenant/crm/loss-reasons',      module: 'crm', menuKeys: ['crm_param_loss_reasons'],     accent: 'text-emerald-600' },
  { key: 'crm_proposal_types', label: 'Tipos Proposta',       icon: FileText,        route: '/tenant/crm/proposal-types',    module: 'crm', menuKeys: ['crm_param_proposal_types'],   accent: 'text-emerald-600' },
  { key: 'crm_lead_sources',   label: 'Origem Leads',         icon: Megaphone,       route: '/tenant/crm/lead-sources',      module: 'crm', menuKeys: ['crm_param_lead_sources'],     accent: 'text-emerald-600' },
  { key: 'crm_workspaces',     label: 'Workspace',            icon: Layers,          route: '/tenant/crm/workspaces',        module: 'crm', menuKeys: ['crm_workspaces'],             accent: 'text-emerald-600' },

  // ---------------- Tarefas ----------------
  { key: 'tasks_list',         label: 'Tarefas',              icon: ClipboardList,   route: '/tenant/tasks/list',            module: 'tasks', menuKeys: ['tasks_list'],                accent: 'text-amber-600' },
  { key: 'tasks_dashboard',    label: 'Dashboard Tarefas',    icon: LayoutDashboard, route: '/tenant/tasks/dashboard',       module: 'tasks', menuKeys: ['tasks_dashboard'],           accent: 'text-amber-600' },
  { key: 'tasks_kanban',       label: 'Kanban Tarefas',       icon: Kanban,          route: '/tenant/tasks/kanban',          module: 'tasks', menuKeys: ['tasks_kanban'],              accent: 'text-amber-600' },

  // ---------------- Financeiro ----------------
  { key: 'fin_charges',        label: 'Cobrança',             icon: Receipt,         route: '/tenant/financial/charges',        module: 'financial', menuKeys: ['financial_charges'],       accent: 'text-rose-600' },
  { key: 'fin_subscriptions',  label: 'Recorrência',          icon: Repeat,          route: '/tenant/financial/subscriptions',  module: 'financial', menuKeys: ['financial_charges'],       accent: 'text-rose-600' },
  { key: 'fin_installments',   label: 'Parcelamentos',        icon: Layers,          route: '/tenant/financial/installments',   module: 'financial', menuKeys: ['financial_charges'],       accent: 'text-rose-600' },
  { key: 'fin_dashboard',      label: 'Dashboard Financeiro', icon: Wallet,          route: '/tenant/financial/dashboard',      module: 'financial', menuKeys: ['financial_dashboard'],     accent: 'text-rose-600' },
  { key: 'fin_payment_links',  label: 'Link Pagamento',       icon: Link2,           route: '/tenant/financial/payment-links',  module: 'financial', menuKeys: ['financial_charges'],       accent: 'text-rose-600' },
  { key: 'fin_billing_reminders', label: 'Régua Cobrança',    icon: AlarmClock,      route: '/tenant/financial/billing-reminders', module: 'financial', menuKeys: ['financial_billing_reminders'], accent: 'text-rose-600' },
  { key: 'fin_accounts',       label: 'Conta Corrente',       icon: CreditCard,      route: '/tenant/financial/accounts',       module: 'financial', menuKeys: ['financial_accounts'],      accent: 'text-rose-600' },

  // ---------------- Indicação ----------------
  // NOTA: `referral_dashboard` mantém a chave (compat com favoritos salvos),
  // mas passa a apontar para a LISTAGEM de indicações; o Dashboard vira
  // um atalho separado (`referral_dash`).
  { key: 'referral_dashboard', label: 'Indicação',            icon: Handshake,       route: '/tenant/indicacao/indicacoes',   module: 'referral', menuKeys: ['referral_referrals'],       accent: 'text-fuchsia-600' },
  { key: 'referral_dash',      label: 'Dashboard Indicações', icon: LayoutDashboard, route: '/tenant/indicacao',              module: 'referral', menuKeys: ['referral_dashboard'],       accent: 'text-fuchsia-600' },
  { key: 'referral_affiliates',label: 'Afiliados',            icon: UserPlus,        route: '/tenant/indicacao/afiliados',    module: 'referral', menuKeys: ['referral_affiliates'],      accent: 'text-fuchsia-600' },
  { key: 'referral_commissions', label: 'Comissões',          icon: Percent,         route: '/tenant/indicacao/comissoes',    module: 'referral', menuKeys: ['referral_commissions'],     accent: 'text-fuchsia-600' },
  { key: 'referral_payments',  label: 'Pagamentos',           icon: Wallet,          route: '/tenant/indicacao/pagamentos',   module: 'referral', menuKeys: ['referral_payouts'],         accent: 'text-fuchsia-600' },
  { key: 'referral_programs',  label: 'Programas',            icon: Handshake,       route: '/tenant/indicacao/programas',    module: 'referral', menuKeys: ['referral_programs'],        accent: 'text-fuchsia-600' },
  { key: 'referral_campaigns', label: 'Campanhas',            icon: Megaphone,       route: '/tenant/indicacao/campanhas',    module: 'referral', menuKeys: ['referral_campaigns'],       accent: 'text-fuchsia-600' },
  { key: 'referral_materials', label: 'Materiais',            icon: ImageIcon,       route: '/tenant/indicacao/materiais',    module: 'referral', menuKeys: ['referral_materials'],       accent: 'text-fuchsia-600' },

  // ---------------- Wiki / KB ----------------
  { key: 'kb_spaces',          label: 'Base Conhecim.',       icon: BookOpen,        route: '/tenant/kb',                     module: 'knowledge',                                      accent: 'text-teal-600' },

  // ---------------- Configuração (v1.4.0: gate requireAdmin) ----------------
  { key: 'tenant_settings',      label: 'Configurações',      icon: Settings,        route: '/tenant/settings',               module: 'shared', requireAdmin: true,                    accent: 'text-slate-600' },
  { key: 'settings_permissions', label: 'Permissões',         icon: ShieldCheck,     route: '/tenant/permissions',            module: 'shared', requireAdmin: true,                    accent: 'text-slate-600' },
  { key: 'settings_company',     label: 'Empresa',            icon: Building2,       route: '/tenant/company',                module: 'shared', requireAdmin: true,                    accent: 'text-slate-600' },
  { key: 'settings_agents',      label: 'Agentes',            icon: Users,           route: '/tenant/agents',                 module: 'shared', menuKeys: ['agents'],                  accent: 'text-slate-600' },

  // ======================= v1.5.0 — módulos criados após o launcher =======================
  // CRM
  { key: 'crm_proposals',                  label: 'Propostas',            icon: FileText,      route: '/tenant/crm/proposals',                         module: 'crm', menuKeys: ['crm_proposals', 'crm_proposals_list'], accent: 'text-emerald-600' },
  { key: 'crm_proposals_kanban',           label: 'Kanban Propostas',     icon: Kanban,        route: '/tenant/crm/proposals/kanban',                  module: 'crm', menuKeys: ['crm_proposals', 'crm_proposals_kanban'], accent: 'text-emerald-600' },
  { key: 'crm_generated_contracts',        label: 'Contratos Gerados',    icon: FileSignature, route: '/tenant/crm/generated-contracts',               module: 'crm', menuKeys: ['crm_contracts'],         accent: 'text-emerald-600' },
  { key: 'crm_generated_contracts_kanban', label: 'Kanban Contratos',     icon: Kanban,        route: '/tenant/crm/generated-contracts?view=kanban',   module: 'crm', menuKeys: ['crm_contracts'],         accent: 'text-emerald-600' },
  { key: 'crm_price_tables',               label: 'Tabelas de Preço',     icon: Percent,       route: '/tenant/crm/price-tables',                      module: 'crm', menuKeys: ['crm_prices'],            accent: 'text-emerald-600' },
  { key: 'crm_contract_templates',         label: 'Modelos Contrato',     icon: FileSignature, route: '/tenant/crm/contract-templates',                module: 'crm', menuKeys: ['crm_contract_templates'], accent: 'text-emerald-600' },
  { key: 'crm_pipelines',                  label: 'Pipelines',            icon: Layers,        route: '/tenant/crm/pipelines',                         module: 'crm', menuKeys: ['crm_pipelines'],         accent: 'text-emerald-600' },
  // Financeiro / Indicação
  { key: 'fin_payment_methods',            label: 'Formas Pagamento',     icon: CreditCard,    route: '/tenant/financial/payment-methods',             module: 'financial', menuKeys: ['financial_payment_methods'], accent: 'text-rose-600' },
  { key: 'referral_contract_templates',    label: 'Modelos Contrato Indicação', icon: FileSignature, route: '/tenant/indicacao/modelos-contrato',      module: 'referral', menuKeys: ['referral_programs'], accent: 'text-fuchsia-600' },
  // Veículos
  { key: 'vehicles_dashboard',             label: 'Dashboard Veículos',   icon: LayoutDashboard, route: '/tenant/vehicles/dashboard',                  module: 'vehicles', menuKeys: ['vehicles_list'],    accent: 'text-indigo-600' },
  { key: 'vehicles_list',                  label: 'Veículos',             icon: Car,           route: '/tenant/vehicles/list',                         module: 'vehicles', menuKeys: ['vehicles_list'],    accent: 'text-indigo-600' },
  { key: 'vehicles_inspections',           label: 'Vistorias',            icon: ClipboardCheck, route: '/tenant/vehicles/inspections',                 module: 'vehicles', menuKeys: ['vehicles_inspections'], accent: 'text-indigo-600' },
  { key: 'vehicles_inspections_kanban',    label: 'Kanban Vistorias',     icon: Kanban,        route: '/tenant/vehicles/inspections/kanban',           module: 'vehicles', menuKeys: ['vehicles_inspections_kanban', 'vehicles_inspections'], accent: 'text-indigo-600' },
  { key: 'vehicles_map',                   label: 'Mapa Veículos',        icon: MapPin,        route: '/tenant/vehicles/map',                          module: 'vehicles', menuKeys: ['vehicles_list'],    accent: 'text-indigo-600' },
  { key: 'vehicles_statuses',              label: 'Status Veículo',       icon: Tag,           route: '/tenant/vehicles/statuses',                     module: 'vehicles', menuKeys: ['vehicles_categories'], accent: 'text-indigo-600' },
  { key: 'vehicles_categories',            label: 'Categorias Veículo',   icon: Layers,        route: '/tenant/vehicles/categories',                   module: 'vehicles', menuKeys: ['vehicles_categories'], accent: 'text-indigo-600' },
  { key: 'vehicles_checklist_templates',   label: 'Modelos Checklist',    icon: ListChecks,    route: '/tenant/settings/checklist-templates',          module: 'vehicles', menuKeys: ['checklist_templates'], accent: 'text-indigo-600' },
  // v1.6.0: Ordem de Serviço / Gestão Rastreador / Logística
  { key: 'os_dashboard', label: 'Dashboard O.S.', icon: LayoutDashboard, route: '/tenant/service-orders/dashboard', module: 'serviceOrders', menuKeys: ['os_list'], accent: 'text-amber-600' },
  { key: 'os_list', label: 'Ordens de Serviço', icon: List, route: '/tenant/service-orders/list', module: 'serviceOrders', menuKeys: ['os_list'], accent: 'text-amber-600' },
  { key: 'os_categories', label: 'Categorias de OS', icon: Tags, route: '/tenant/service-orders/categories', module: 'serviceOrders', menuKeys: ['os_categories'], accent: 'text-amber-600' },
  { key: 'os_phases', label: 'Fases de OS', icon: Layers, route: '/tenant/service-orders/phases', module: 'serviceOrders', menuKeys: ['os_phases'], accent: 'text-amber-600' },
  { key: 'os_phase_permissions', label: 'Permissões Por Fase', icon: Shield, route: '/tenant/service-orders/phase-permissions', module: 'serviceOrders', menuKeys: ['os_phases'], accent: 'text-amber-600' },
  { key: 'os_service_types', label: 'Tipos de Serviço', icon: Wrench, route: '/tenant/service-orders/service-types', module: 'serviceOrders', menuKeys: ['os_service_types'], accent: 'text-amber-600' },
  { key: 'stock_overview', label: 'Visão Geral Rastreador', icon: LayoutDashboard, route: '/tenant/stock/overview', module: 'stock', menuKeys: ['stock_overview'], accent: 'text-teal-600' },
  { key: 'stock_movements', label: 'Movimentações', icon: Route, route: '/tenant/stock/movements', module: 'stock', menuKeys: ['stock_movements'], accent: 'text-teal-600' },
  { key: 'stock_trackers', label: 'Rastreadores', icon: Cpu, route: '/tenant/stock/trackers', module: 'stock', menuKeys: ['stock_trackers'], accent: 'text-teal-600' },
  { key: 'stock_kits', label: 'Kits', icon: Boxes, route: '/tenant/stock/kits', module: 'stock', menuKeys: ['stock_kits'], accent: 'text-teal-600' },
  { key: 'stock_locations', label: 'Endereços de Estoque', icon: MapPinned, route: '/tenant/stock/locations', module: 'stock', menuKeys: ['stock_locations'], accent: 'text-teal-600' },
  { key: 'stock_homologations', label: 'Homologação', icon: BadgeCheck, route: '/tenant/stock/homologations', module: 'stock', menuKeys: ['stock_homologations'], accent: 'text-teal-600' },
  { key: 'stock_platforms', label: 'Plataformas', icon: Monitor, route: '/tenant/stock/platforms', module: 'stock', menuKeys: ['stock_platforms'], accent: 'text-teal-600' },
  { key: 'stock_models', label: 'Modelos de Rastreador', icon: Cpu, route: '/tenant/stock/models', module: 'stock', menuKeys: ['stock_models'], accent: 'text-teal-600' },
  { key: 'stock_chips', label: 'Chips', icon: Layers, route: '/tenant/stock/chips', module: 'stock', menuKeys: ['stock_chips'], accent: 'text-teal-600' },
  { key: 'stock_data_plans', label: 'Planos de dados', icon: Route, route: '/tenant/stock/data-plans', module: 'stock', menuKeys: ['stock_data_plans'], accent: 'text-teal-600' },
  { key: 'stock_chip_cancellations', label: 'Cancelamento Chips', icon: Ban, route: '/tenant/stock/chip-cancellations', module: 'stock', menuKeys: ['stock_chip_cancellations'], accent: 'text-teal-600' },
  { key: 'log_shipments', label: 'Remessas', icon: Truck, route: '/tenant/logistics/shipments', module: 'logistics', menuKeys: ['log_shipments'], accent: 'text-sky-600' },
  { key: 'log_shipments_kanban', label: 'Kanban Remessas', icon: Kanban, route: '/tenant/logistics/shipments/kanban', module: 'logistics', menuKeys: ['log_shipments'], accent: 'text-sky-600' },
  // Retenção / Jurídico
  { key: 'retention_dashboard',            label: 'Dashboard Retenção',   icon: LayoutDashboard, route: '/tenant/retention/dashboard',                 module: 'retention', menuKeys: ['retention_dashboard'], accent: 'text-orange-600' },
  { key: 'retention_kanban',               label: 'Kanban Retenção',      icon: Kanban,        route: '/tenant/retention/kanban',                      module: 'retention', menuKeys: ['retention_kanban'], accent: 'text-orange-600' },
  { key: 'retention_list',                 label: 'Lista Retenção',       icon: ClipboardList, route: '/tenant/retention/list',                        module: 'retention', menuKeys: ['retention_list'],  accent: 'text-orange-600' },
  { key: 'legal_dashboard',                label: 'Dashboard Jurídico',   icon: LayoutDashboard, route: '/tenant/legal/dashboard',                     module: 'legal', menuKeys: ['legal_dashboard'],     accent: 'text-violet-600' },
  { key: 'legal_kanban',                   label: 'Kanban Jurídico',      icon: Kanban,        route: '/tenant/legal/kanban',                          module: 'legal', menuKeys: ['legal_kanban'],        accent: 'text-violet-600' },
  { key: 'legal_list',                     label: 'Lista Jurídico',       icon: Scale,         route: '/tenant/legal/list',                            module: 'legal', menuKeys: ['legal_list'],          accent: 'text-violet-600' },
  // Workflow
  { key: 'workflow_processes',             label: 'Workflow',             icon: GitBranch,     route: '/tenant/workflow',                              module: 'workflow', menuKeys: ['workflow_processes'], accent: 'text-cyan-600' },
  // Dados
  { key: 'data_connections',               label: 'Conexões Dados',       icon: Database,      route: '/tenant/data/connections',                      module: 'shared', menuKeys: ['data_connections'],   accent: 'text-slate-600' },
  { key: 'data_datasets',                  label: 'Datasets',             icon: Database,      route: '/tenant/data/datasets',                         module: 'shared', menuKeys: ['data_datasets'],      accent: 'text-slate-600' },
  { key: 'data_explorer',                  label: 'Explorer Dados',       icon: Search,        route: '/tenant/data/explorer',                         module: 'shared', menuKeys: ['data_explorer'],      accent: 'text-slate-600' },
  { key: 'data_actions',                   label: 'Ações Dados',          icon: Zap,           route: '/tenant/data/actions',                          module: 'shared', menuKeys: ['data_connections'],   accent: 'text-slate-600' },
  { key: 'data_sync_history',              label: 'Histórico Sync',       icon: History,       route: '/tenant/data/sync-history',                     module: 'shared', menuKeys: ['data_sync_history'],  accent: 'text-slate-600' },
  // Distribuição / Conteúdo / Base
  { key: 'distribution_queues',            label: 'Filas Distribuição',   icon: ListOrdered,   route: '/tenant/distribution/queues',                   module: 'shared', menuKeys: ['distribution_queues', 'distribution'], accent: 'text-sky-600' },
  { key: 'departments',                    label: 'Departamentos',        icon: Building2,     route: '/tenant/departments',                           module: 'shared', menuKeys: ['departments'],        accent: 'text-sky-600' },
  { key: 'content_templates',              label: 'Modelos Conteúdo',     icon: FileText,      route: '/tenant/content/templates',                     module: 'shared', requireAdmin: true,               accent: 'text-sky-600' },
  { key: 'kb_extranet',                    label: 'Extranet',             icon: BookOpen,      route: '/tenant/kb/extranet',                           module: 'knowledge', menuKeys: ['kb_extranet'],     accent: 'text-teal-600' },
  // Configuração
  { key: 'settings_exports',               label: 'Exportações',          icon: Download,      route: '/tenant/settings/exports',                      module: 'shared', requireAdmin: true,               accent: 'text-slate-600' },
  { key: 'settings_plans',                 label: 'Planos',               icon: ShieldCheck,   route: '/tenant/plans',                                 module: 'shared', requireAdmin: true,               accent: 'text-slate-600' },
];

/** Lookup rápido por chave. */
export const SHORTCUTS_BY_KEY: Record<string, AppShortcutDef> = APP_SHORTCUTS.reduce(
  (acc, s) => {
    acc[s.key] = s;
    return acc;
  },
  {} as Record<string, AppShortcutDef>,
);

/**
 * Categorias exibidas no popover "Todos os atalhos".
 * O primeiro bloco tem `title: null` (o próprio título "Todos os atalhos" já
 * está renderizado acima dele no popover). Categorias com todas as chaves
 * filtradas (sem permissão/módulo) são omitidas em runtime.
 */
export interface ShortcutCategory {
  key: string;
  /** null = sem cabeçalho (reaproveita o título "Todos os atalhos"). */
  title: string | null;
  shortcutKeys: string[];
}

export const SHORTCUT_CATEGORIES: ShortcutCategory[] = [
  {
    key: 'conversations',
    title: null,
    shortcutKeys: [
      'chat_inbox',
      'chat_kanban',
      'chat_dashboard',
      'contacts',
      'lists',
      'campaigns',
      'flows',
      'routing',
      'service_queue',
      'calendar',
      'reminders_center',
      'scheduled_messages',
      'templates',
      'inboxes',
    ],
  },
  {
    key: 'business',
    title: 'Negócio',
    shortcutKeys: [
      'crm_deals',
      'crm_dashboard',
      'crm_contracts',
      'crm_customers',
      'crm_products',
      'scheduling',
      'crm_goals',
      'crm_sellers',
      'crm_playbooks',
      'crm_loss_reasons',
      'crm_lead_sources',
      'crm_workspaces',
      // v1.5.0
      'crm_proposals',
      'crm_proposals_kanban',
      'crm_generated_contracts',
      'crm_generated_contracts_kanban',
      'crm_price_tables',
      'crm_contract_templates',
      'crm_pipelines',
    ],
  },
  {
    key: 'activities',
    title: 'Atividades',
    shortcutKeys: [
      'activities_feed',
      'activities_list_page',
      'activities_feed_page',
      'activities_calendar',
    ],
  },
  {
    key: 'tasks',
    title: 'Tarefas',
    shortcutKeys: ['tasks_list', 'tasks_dashboard', 'tasks_kanban'],
  },
  {
    key: 'financial',
    title: 'Financeiro',
    shortcutKeys: [
      'fin_charges',
      'fin_subscriptions',
      'fin_installments',
      'fin_dashboard',
      'fin_payment_links',
      'fin_billing_reminders',
      'fin_accounts',
      'fin_payment_methods',
    ],
  },
  {
    key: 'referral',
    title: 'Indicação',
    shortcutKeys: [
      'referral_dashboard',
      'referral_dash',
      'referral_affiliates',
      'referral_commissions',
      'referral_payments',
      'referral_programs',
      'referral_campaigns',
      'referral_materials',
      'referral_contract_templates',
    ],
  },
  // v1.5.0: novas categorias de módulos criados após o launcher
  {
    key: 'vehicles',
    title: 'Veículos',
    shortcutKeys: [
      'vehicles_dashboard',
      'vehicles_list',
      'vehicles_inspections',
      'vehicles_inspections_kanban',
      'vehicles_map',
      'vehicles_statuses',
      'vehicles_categories',
      'vehicles_checklist_templates',
    ],
  },
  // v1.6.0
  { key: 'serviceOrders', title: 'Ordem de Serviço', shortcutKeys: ['os_list', 'os_categories', 'os_phases', 'os_phase_permissions', 'os_service_types'] },
  { key: 'stock', title: 'Gestão Rastreador', shortcutKeys: ['stock_overview', 'stock_movements', 'stock_trackers', 'stock_kits', 'stock_locations', 'stock_homologations', 'stock_platforms', 'stock_models', 'stock_chips', 'stock_data_plans', 'stock_chip_cancellations'] },
  { key: 'logistics', title: 'Logística', shortcutKeys: ['log_shipments', 'log_shipments_kanban'] },
  {
    key: 'retention',
    title: 'Retenção',
    shortcutKeys: ['retention_dashboard', 'retention_kanban', 'retention_list'],
  },
  {
    key: 'legal',
    title: 'Jurídico',
    shortcutKeys: ['legal_dashboard', 'legal_kanban', 'legal_list'],
  },
  {
    key: 'workflow',
    title: 'Workflow',
    shortcutKeys: ['workflow_processes'],
  },
  {
    key: 'data',
    title: 'Dados',
    shortcutKeys: [
      'data_connections',
      'data_datasets',
      'data_explorer',
      'data_actions',
      'data_sync_history',
    ],
  },
  {
    key: 'distribution',
    title: 'Distribuição e Conteúdo',
    shortcutKeys: [
      'distribution_queues',
      'departments',
      'content_templates',
      'kb_extranet',
    ],
  },
  {
    key: 'settings',
    title: 'Configuração',
    shortcutKeys: [
      'tenant_settings',
      'settings_permissions',
      'settings_company',
      'settings_agents',
      // v1.5.0
      'settings_exports',
      'settings_plans',
    ],
  },
];
